<template>
  <div class="loading" v-if="isLoading">Loading {{ loadingMessage }}...</div>
  <div v-else class="bulk-invitations">
    <!-- Upload -->
    <div
      class="upload-zone"
      :class="{ dragover: isDragover }"
      @click="$refs.fileInput.click()"
      @dragover.prevent="isDragover = true"
      @dragleave="isDragover = false"
      @drop.prevent="handleDrop"
    >
      <input ref="fileInput" type="file" accept=".csv" @change="handleFile" />
      <div class="icon">&#128196;</div>
      <div class="label" v-if="!csvFile">
        Drop your stallholder CSV here or click to browse
      </div>
      <div class="label" v-else>
        {{ csvFile.name }} &mdash; {{ stallholders.length }} stall entries
        loaded
      </div>
      <div class="hint">
        Expected columns: Stall #, Stallholder Name, Market Day, Stall Type,
        Email ID
      </div>
    </div>

    <!-- Config -->
    <div class="config-section">
      <h3>Configuration</h3>
      <div class="config-row">
        <label>Start date:</label>
        <input type="date" v-model="revealDate" class="date-input" />
        <span class="config-hint"
          >Only market dates on or after this date will be included</span
        >
      </div>
      <div class="btn-bar">
        <button @click="fetchDates" :disabled="loading" class="btn btn-primary">
          {{ loading ? "Fetching..." : "Fetch Market Dates" }}
        </button>
        <button
          @click="resolveIds"
          :disabled="resolving || !stallholders.length"
          class="btn btn-secondary"
        >
          {{ resolving ? "Resolving..." : "Resolve Stall & Stallholder IDs" }}
        </button>
        <button
          @click="confirmAndSend"
          :disabled="!canSend || sending"
          class="btn btn-success"
        >
          {{ sending ? "Sending..." : "Review & Send Bookings" }}
        </button>
        <button
          v-if="groupedStallholders.length && marketDates.length"
          @click="exportCSV"
          class="btn btn-secondary"
        >
          Export CSV
        </button>
      </div>
    </div>

    <!-- Status -->
    <div class="status-bar" v-if="statusMsg">
      <span
        class="dot"
        :class="{
          'dot-ok': statusType === 'ok',
          'dot-err': statusType === 'err',
          'dot-load': statusType === 'load',
        }"
      ></span>
      {{ statusMsg }}
    </div>

    <!-- Resolve progress -->
    <div
      class="progress-bar-container"
      v-if="resolveProgress.total > 0 && resolving"
    >
      <div class="progress-text">
        {{ resolveProgress.phase }}: {{ resolveProgress.done }} /
        {{ resolveProgress.total }}
      </div>
      <div class="progress-track">
        <div
          class="progress-fill"
          :style="{
            width: (resolveProgress.done / resolveProgress.total) * 100 + '%',
          }"
        ></div>
      </div>
    </div>

    <!-- Send progress -->
    <div class="progress-bar-container" v-if="sendProgress.total > 0">
      <div class="progress-text">
        Sending bookings: {{ sendProgress.done }} / {{ sendProgress.total }}
      </div>
      <div class="progress-track">
        <div
          class="progress-fill"
          :class="{ 'has-errors': sendProgress.errors > 0 }"
          :style="{
            width: (sendProgress.done / sendProgress.total) * 100 + '%',
          }"
        ></div>
      </div>
      <div
        class="results-summary"
        v-if="sendProgress.done === sendProgress.total"
      >
        <span class="success-count">{{ sendProgress.success }} succeeded</span>,
        <span class="error-count">{{ sendProgress.errors }} failed</span>
      </div>
    </div>

    <!-- Error log -->
    <div class="error-log" v-if="sendErrors.length">
      <div class="error-log-header">
        <h2>Failed Bookings ({{ sendErrors.length }})</h2>
        <button class="btn btn-sm btn-secondary" @click="sendErrors = []">
          Clear
        </button>
      </div>
      <div class="error-log-item" v-for="(err, idx) in sendErrors" :key="idx">
        <div class="error-log-who">
          <strong>{{ err.name }}</strong>
          <span class="error-code">{{ err.code }}</span>
          <span class="error-status" v-if="err.status"
            >HTTP {{ err.status }}</span
          >
        </div>
        <div class="error-log-messages">
          <div
            v-for="(msg, mIdx) in err.messages"
            :key="mIdx"
            class="error-msg"
          >
            {{ msg }}
          </div>
        </div>
      </div>
    </div>

    <!-- Summary -->
    <div class="summary-row" v-if="stallholders.length && marketDates.length">
      <div class="summary-card">
        <div class="num">{{ stallholders.length }}</div>
        <div class="desc">Stall entries</div>
      </div>
      <div class="summary-card">
        <div class="num">{{ groupedStallholders.length }}</div>
        <div class="desc">Unique stallholders</div>
      </div>
      <div class="summary-card">
        <div class="num">{{ weekendDates.length }}</div>
        <div class="desc">Weekend dates</div>
      </div>
      <div class="summary-card">
        <div class="num">{{ totalBookings }}</div>
        <div class="desc">Bookings to create</div>
      </div>
      <div class="summary-card">
        <div class="num">{{ resolvedStallholderCount }}</div>
        <div class="desc">Stallholders resolved</div>
      </div>
      <div class="summary-card">
        <div class="num" style="color: #c4572a">
          {{ unresolvedStallholderCount }}
        </div>
        <div class="desc">Stallholders missing</div>
      </div>
      <div class="summary-card" v-if="unresolvedStallCount > 0">
        <div class="num" style="color: #c4572a">{{ unresolvedStallCount }}</div>
        <div class="desc">Stalls missing</div>
      </div>
    </div>

    <!-- Filters -->
    <div
      class="filter-bar"
      v-if="groupedStallholders.length && marketDates.length"
    >
      <span class="filter-label">Filter:</span>
      <span
        class="filter-chip"
        :class="{ active: filter === 'all' }"
        @click="filter = 'all'"
        >All</span
      >
      <span
        class="filter-chip"
        :class="{ active: filter === 'sat-sun' }"
        @click="filter = 'sat-sun'"
        >Sat &amp; Sun</span
      >
      <span
        class="filter-chip"
        :class="{ active: filter === 'sat' }"
        @click="filter = 'sat'"
        >Sat only</span
      >
      <span
        class="filter-chip"
        :class="{ active: filter === 'sun' }"
        @click="filter = 'sun'"
        >Sun only</span
      >
      <span
        class="filter-chip"
        :class="{ active: filter === '2nd-sun' }"
        @click="filter = '2nd-sun'"
        >2nd Sun</span
      >
      <span
        class="filter-chip"
        :class="{ active: filter === 'unresolved' }"
        @click="filter = 'unresolved'"
        >Unresolved IDs</span
      >
      <input
        type="text"
        class="search-input"
        placeholder="Search stallholders..."
        v-model="searchQuery"
      />
    </div>

    <!-- Unresolved stallholders alert -->
    <div class="unresolved-section" v-if="unresolvedStallholders.length">
      <div class="unresolved-header">
        <h2>Stallholders Without ID ({{ unresolvedStallholders.length }})</h2>
        <p>
          These stallholders could not be matched to a stallholder record via
          email search.
        </p>
      </div>
      <table class="unresolved-table">
        <thead>
          <tr>
            <th style="width: 40px">#</th>
            <th>Name</th>
            <th>Email</th>
            <th>Market Day</th>
            <th>Stall Type</th>
            <th>Stalls</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(sh, idx) in unresolvedStallholders" :key="sh.key">
            <td>{{ idx + 1 }}</td>
            <td>
              <strong>{{ sh.name }}</strong>
            </td>
            <td>
              <span v-if="sh.email" class="email-mono">{{ sh.email }}</span>
              <span v-else class="no-email">No email</span>
            </td>
            <td>
              <span class="market-day-tag" :class="dayTagClass(sh.marketDay)">{{
                sh.marketDay
              }}</span>
            </td>
            <td>
              <span class="stall-type-label">{{ sh.stallType }}</span>
            </td>
            <td>
              <span
                class="stall-badge"
                :class="stall.resolvedId ? 'resolved' : 'unresolved'"
                v-for="stall in sh.stalls"
                :key="stall.name"
                >{{ stall.name }}</span
              >
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Stallholder cards -->
    <template v-if="filteredStallholders.length">
      <div class="expand-controls">
        <button class="btn btn-sm btn-secondary" @click="expandAll">
          Expand All
        </button>
        <button class="btn btn-sm btn-secondary" @click="collapseAll">
          Collapse All
        </button>
      </div>
      <div
        class="stallholder-card"
        :class="{ 'has-errors': sh.hasError }"
        v-for="sh in filteredStallholders"
        :key="sh.key"
      >
        <div
          class="stallholder-header"
          :class="{ 'header-error': sh.hasError }"
          @click="toggleCard(sh.key)"
        >
          <span class="collapse-icon">{{
            expandedCards[sh.key] ? "&#9660;" : "&#9654;"
          }}</span>
          <h2>{{ sh.name }}</h2>
          <span v-if="sh.hasError" class="error-notice">MISSING IDs</span>
          <span class="header-summary"
            >{{ sh.bookingCount }} booking{{
              sh.bookingCount !== 1 ? "s" : ""
            }}
            &middot; {{ sh.stalls.length }} stall{{
              sh.stalls.length !== 1 ? "s" : ""
            }}</span
          >
          <div class="stallholder-meta">
            <span class="market-day-tag" :class="dayTagClass(sh.marketDay)">{{
              sh.marketDay
            }}</span>
            <span class="stall-type-label">{{ sh.stallType }}</span>
            <span
              class="id-tag"
              :class="sh.stallholderId ? 'id-resolved' : 'id-missing'"
            >
              {{
                sh.stallholderId
                  ? "ID: " + sh.stallholderId
                  : "No stallholder ID"
              }}
            </span>
            <a
              v-if="sh.email"
              :href="'mailto:' + sh.email"
              class="email-link"
              @click.stop
              >{{ sh.email }}</a
            >
          </div>
        </div>

        <template v-if="expandedCards[sh.key]">
          <div class="stalls-list">
            <strong>Stalls:</strong>
            <span
              class="stall-badge"
              :class="stall.resolvedId ? 'resolved' : 'unresolved'"
              v-for="stall in sh.stalls"
              :key="stall.name"
            >
              {{ stall.name }}
              <template v-if="stall.resolvedId">
                (#{{ stall.resolvedId }})</template
              >
              <template v-else>
                (?)</template
              >
            </span>
          </div>

          <table class="dates-table">
            <thead>
              <tr>
                <th style="width: 40px">#</th>
                <th>Market Date</th>
                <th>Day</th>
                <th>Week</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(md, idx) in sh.matchedDates" :key="md.date">
                <td>{{ idx + 1 }}</td>
                <td>{{ formatDate(md.date) }}</td>
                <td>{{ md.dayName }}</td>
                <td>
                  <span class="week-label">{{ md.weekLabel }}</span>
                </td>
              </tr>
            </tbody>
          </table>

          <div class="batch-payload-preview">
            <strong
              >Batch payload (1 request,
              {{ sh.bookingCount }} bookings):</strong
            >
            <pre>{{ JSON.stringify(sh.batchPayload, null, 2) }}</pre>
          </div>

          <div class="date-count">
            <span
              >{{ sh.matchedDates.length }} date{{
                sh.matchedDates.length !== 1 ? "s" : ""
              }}
              &times; {{ sh.stalls.length }} stall{{
                sh.stalls.length !== 1 ? "s" : ""
              }}
              = {{ sh.bookingCount }} booking{{
                sh.bookingCount !== 1 ? "s" : ""
              }}</span
            >
            <span
              v-if="sh.stallholderId && sh.stalls.every((s) => s.resolvedId)"
              >Ready to send</span
            >
            <span v-else style="color: #c4572a">Missing IDs</span>
          </div>
        </template>
      </div>
    </template>

    <div
      class="empty-state"
      v-if="
        stallholders.length &&
          marketDates.length &&
          !filteredStallholders.length
      "
    >
      <p>No stallholders match the current filter.</p>
    </div>

    <div class="empty-state" v-if="!stallholders.length || !marketDates.length">
      <p>Upload a CSV and fetch market dates to get started.</p>
    </div>
  </div>
</template>

<script>
import Papa from "papaparse";

export default {
  name: "BulkInvitations",

  data() {
    return {
      isLoading: false,
      loadingMessage: "",
      csvFile: null,
      stallholders: [],
      marketDates: [],
      revealDate: "",
      datesEndpoint: `/wp-json/wisdom-markets/v1/markets/?from=${new Date().getFullYear()}-01-01&to=${new Date().getFullYear() +
        1}-12-31`,
      loading: false,
      resolving: false,
      sending: false,
      statusMsg: "",
      statusType: "",
      isDragover: false,
      filter: "all",
      searchQuery: "",
      expandedCards: {},
      stallIdMap: {},
      stallholderIdMap: {},
      sendProgress: {
        total: 0,
        done: 0,
        success: 0,
        errors: 0,
      },
      sendErrors: [],
      resolveProgress: {
        total: 0,
        done: 0,
        phase: "",
      },
    };
  },

  computed: {
    accessToken() {
      return `Bearer ${this.$store.state.accessToken}`;
    },

    domain() {
      return process.env.VUE_APP_DOMAIN;
    },

    weekendDates() {
      const revealCutoff = this.revealDate
        ? this.parseDate(this.revealDate)
        : null;
      const results = [];

      this.marketDates.forEach((md) => {
        const d = this.parseDate(md.date);
        if (revealCutoff && d < revealCutoff) return;
        const dayOfWeek = d.getDay();
        if (dayOfWeek !== 0 && dayOfWeek !== 6) return;
        const isSat = dayOfWeek === 6;
        const weekNum = this.getWeekOfMonth(d);
        results.push({
          date: md.date,
          isSat,
          isSun: !isSat,
          weekNum,
          dayName: isSat ? "Saturday" : "Sunday",
          weekLabel: this.buildWeekLabel(weekNum, isSat),
        });
      });

      return results;
    },

    groupedStallholders() {
      if (!this.stallholders.length || !this.weekendDates.length) return [];

      const map = {};

      this.stallholders.forEach((s) => {
        const key = s.name.toLowerCase().trim();
        if (!map[key]) {
          map[key] = {
            key,
            name: s.name,
            marketDay: s.marketDay,
            stallType: s.stallType,
            email: s.email,
            stalls: [],
            stallNames: [],
          };
        }
        if (!map[key].stallNames.includes(s.stall)) {
          map[key].stallNames.push(s.stall);
          map[key].stalls.push({
            name: s.stall,
            resolvedId:
              this.stallIdMap[s.stall.toLowerCase().trim()] ||
              this.stallIdMap[
                s.stall
                  .toLowerCase()
                  .trim()
                  .replace(/\s+/g, "")
              ] ||
              null,
          });
        }
        if (!map[key].email && s.email) {
          map[key].email = s.email;
        }
      });

      const list = [];

      Object.keys(map).forEach((key) => {
        const sh = map[key];
        sh.stalls.sort((a, b) =>
          a.name.localeCompare(b.name, undefined, { numeric: true }),
        );

        sh.stallholderId = sh.email
          ? this.stallholderIdMap[sh.email.toLowerCase().trim()] || null
          : null;

        sh.matchedDates = this.getMatchedDates(sh);

        const stallIds = sh.stalls
          .map((s) => s.resolvedId)
          .filter((id) => !!id);
        const dateStrings = sh.matchedDates.map((wd) => wd.date);

        sh.batchPayload = {
          stall_id: stallIds.length === 1 ? stallIds[0] : stallIds,
          market_date: dateStrings.length === 1 ? dateStrings[0] : dateStrings,
          products: {},
          stallholder_id: sh.stallholderId || null,
          booking_type: "invitation",
        };

        sh.bookingCount = sh.matchedDates.length * sh.stalls.length;
        sh.hasError = !sh.stallholderId || sh.stalls.some((s) => !s.resolvedId);
        list.push(sh);
      });

      list.sort((a, b) => a.name.localeCompare(b.name));
      return list;
    },

    filteredStallholders() {
      let list = this.groupedStallholders;

      if (this.filter === "unresolved") {
        list = list.filter(
          (sh) => !sh.stallholderId || sh.stalls.some((s) => !s.resolvedId),
        );
      } else if (this.filter !== "all") {
        list = list.filter((sh) => {
          const md = sh.marketDay.toLowerCase().replace(/\s+/g, "");
          if (this.filter === "sat-sun")
            return md.includes("sat") && md.includes("sun");
          if (this.filter === "sat")
            return (
              md.includes("sat") && !md.includes("sun") && !md.includes("2nd")
            );
          if (this.filter === "sun")
            return (
              md.includes("sun") && !md.includes("sat") && !md.includes("2nd")
            );
          if (this.filter === "2nd-sun")
            return md.includes("2nd") && md.includes("sun");
          return true;
        });
      }

      if (this.searchQuery.trim()) {
        const q = this.searchQuery.toLowerCase().trim();
        list = list.filter(
          (sh) =>
            sh.name.toLowerCase().includes(q) ||
            sh.email.toLowerCase().includes(q) ||
            sh.stallNames.some((st) => st.toLowerCase().includes(q)),
        );
      }

      // Sort errors to top
      list.sort((a, b) => {
        if (a.hasError && !b.hasError) return -1;
        if (!a.hasError && b.hasError) return 1;
        return a.name.localeCompare(b.name);
      });

      return list;
    },

    totalBookings() {
      let count = 0;
      this.groupedStallholders.forEach((sh) => {
        count += sh.bookingCount;
      });
      return count;
    },

    resolvedStallholderCount() {
      return this.groupedStallholders.filter((sh) => sh.stallholderId).length;
    },

    unresolvedStallholderCount() {
      return this.groupedStallholders.filter((sh) => !sh.stallholderId).length;
    },

    unresolvedStallCount() {
      let count = 0;
      this.groupedStallholders.forEach((sh) => {
        sh.stalls.forEach((s) => {
          if (!s.resolvedId) count++;
        });
      });
      return count;
    },

    unresolvedStallholders() {
      return this.groupedStallholders.filter((sh) => !sh.stallholderId);
    },

    canSend() {
      return this.groupedStallholders.length > 0 && this.totalBookings > 0;
    },
  },

  methods: {
    handleFile(e) {
      const file = e.target.files[0];
      if (file) this.parseCSV(file);
    },

    toggleCard(key) {
      this.$set(this.expandedCards, key, !this.expandedCards[key]);
    },

    expandAll() {
      this.filteredStallholders.forEach((sh) => {
        this.$set(this.expandedCards, sh.key, true);
      });
    },

    collapseAll() {
      this.expandedCards = {};
    },

    handleDrop(e) {
      this.isDragover = false;
      const file = e.dataTransfer.files[0];
      if (file && file.name.endsWith(".csv")) this.parseCSV(file);
    },

    parseCSV(file) {
      this.csvFile = file;
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          this.stallholders = results.data
            .filter((row) => row["Stall #"] && row["Stallholder Name"])
            .map((row) => ({
              stall: (row["Stall #"] || "").trim(),
              name: (row["Stallholder Name"] || "").trim(),
              marketDay: (row["Market Day"] || "").trim(),
              stallType: (row["Stall Type"] || "").trim(),
              email: (row["Email ID"] || "").trim(),
            }));
          this.setStatus(
            "ok",
            `Loaded ${this.stallholders.length} stall entries from CSV`,
          );

          // Auto-resolve IDs
          this.resolveIds();
        },
        error: (err) => {
          this.setStatus("err", "CSV parse error: " + err.message);
        },
      });
    },

    async fetchDates() {
      this.loading = true;
      this.setStatus("load", "Fetching market dates...");
      try {
        const res = await fetch(this.domain + this.datesEndpoint, {
          headers: { Authorization: this.accessToken },
        });
        if (!res.ok) throw new Error("HTTP " + res.status);
        const data = await res.json();
        // API now returns { id, market_date, ignore_rules }; normalise to .date for downstream use
        this.marketDates = Array.isArray(data)
          ? data.map((d) => ({
              date: d.market_date,
              id: d.id,
              ignoreRules: d.ignore_rules,
            }))
          : [];
        this.setStatus(
          "ok",
          `Loaded ${this.marketDates.length} dates (${
            this.weekendDates.length
          } weekends)`,
        );
      } catch (err) {
        this.setStatus("err", "Failed to fetch dates: " + err.message);
      } finally {
        this.loading = false;
      }
    },

    async resolveIds() {
      this.resolving = true;
      this.resolveProgress = { total: 0, done: 0, phase: "" };
      this.setStatus("load", "Resolving stall IDs...");

      try {
        // 1. Fetch all stalls from WP REST API
        this.resolveProgress.phase = "Fetching stalls";
        let allStalls = [];
        let page = 1;
        let hasMore = true;

        // Get first page to determine total
        const firstRes = await fetch(
          `${this.domain}/wp-json/wp/v2/stall?per_page=100&page=1`,
          {
            headers: { Authorization: this.accessToken },
          },
        );
        if (!firstRes.ok)
          throw new Error("Stalls fetch failed: HTTP " + firstRes.status);
        const firstData = await firstRes.json();
        allStalls = allStalls.concat(firstData);
        const totalPages =
          parseInt(firstRes.headers.get("X-WP-TotalPages"), 10) || 1;
        this.resolveProgress.total = totalPages;
        this.resolveProgress.done = 1;

        for (page = 2; page <= totalPages; page++) {
          const res = await fetch(
            `${this.domain}/wp-json/wp/v2/stall?per_page=100&page=${page}`,
            {
              headers: { Authorization: this.accessToken },
            },
          );
          if (!res.ok)
            throw new Error("Stalls fetch failed: HTTP " + res.status);
          const data = await res.json();
          allStalls = allStalls.concat(data);
          this.resolveProgress.done = page;
        }

        // Build stall name -> ID map
        const stallMap = {};
        allStalls.forEach((s) => {
          const title = (s.title && s.title.rendered) || s.slug || "";
          const key = title.toLowerCase().trim();
          stallMap[key] = s.id;
          const normalised = key.replace(/\s+/g, "");
          if (normalised !== key) {
            stallMap[normalised] = s.id;
          }
        });
        this.stallIdMap = stallMap;
        this.setStatus(
          "load",
          `Resolved ${allStalls.length} stalls. Now resolving stallholders...`,
        );

        // 2. Resolve stallholder IDs by email
        const uniqueEmails = {};
        this.stallholders.forEach((s) => {
          if (s.email) {
            uniqueEmails[s.email.toLowerCase().trim()] = true;
          }
        });

        const emails = Object.keys(uniqueEmails);
        const shMap = {};
        let resolved = 0;
        let failed = 0;

        this.resolveProgress = {
          total: emails.length,
          done: 0,
          phase: "Resolving stallholders",
        };

        for (let i = 0; i < emails.length; i++) {
          const email = emails[i];
          try {
            const shRes = await fetch(
              `${
                this.domain
              }/wp-json/wisdom-markets/v1/stallholders?s=${encodeURIComponent(
                email,
              )}`,
              {
                headers: { Authorization: this.accessToken },
              },
            );
            if (shRes.ok) {
              const shData = await shRes.json();
              if (Array.isArray(shData) && shData.length > 0) {
                shMap[email] = shData[0].id;
                resolved++;
              } else {
                failed++;
              }
            } else {
              failed++;
            }
          } catch (e) {
            failed++;
          }

          this.resolveProgress.done = i + 1;
        }

        this.stallholderIdMap = shMap;
        this.setStatus(
          "ok",
          `Done. Stalls: ${
            allStalls.length
          } mapped. Stallholders: ${resolved} resolved, ${failed} not found.`,
        );
      } catch (err) {
        this.setStatus("err", "Resolve failed: " + err.message);
      } finally {
        this.resolving = false;
      }
    },

    async confirmAndSend() {
      const validBatches = [];
      let skippedStallholders = 0;
      let totalBookings = 0;

      this.groupedStallholders.forEach((sh) => {
        if (sh.stallholderId && sh.stalls.every((s) => s.resolvedId)) {
          validBatches.push({
            name: sh.name,
            payload: sh.batchPayload,
            bookingCount: sh.bookingCount,
          });
          totalBookings += sh.bookingCount;
        } else {
          skippedStallholders++;
        }
      });

      if (validBatches.length === 0) {
        this.setStatus("err", "No valid batches to send. Resolve IDs first.");
        return;
      }

      let msg = `Ready to send ${
        validBatches.length
      } batch request(s) (${totalBookings} total bookings) to:\n${
        this.domain
      }/wp-json/wisdom-markets/v1/bookings/`;
      if (skippedStallholders > 0) {
        msg += `\n\n${skippedStallholders} stallholder(s) skipped (missing IDs).`;
      }
      msg +=
        "\n\nEach stallholder gets one request with all their dates + stalls.\nOne invitation email per stallholder.\n\nProceed?";

      if (!confirm(msg)) return;

      this.sending = true;
      this.sendErrors = [];
      this.sendProgress = {
        total: validBatches.length,
        done: 0,
        success: 0,
        errors: 0,
      };
      this.setStatus("load", "Sending batch bookings...");

      const endpoint = `${this.domain}/wp-json/wisdom-markets/v1/bookings/`;

      for (let i = 0; i < validBatches.length; i++) {
        const batch = validBatches[i];
        try {
          const res = await fetch(endpoint, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: this.accessToken,
            },
            body: JSON.stringify(batch.payload),
          });

          if (res.ok) {
            this.sendProgress.success++;
          } else {
            this.sendProgress.errors++;
            try {
              const errBody = await res.json();
              const messages = Array.isArray(errBody.message)
                ? errBody.message
                : [errBody.message || "Unknown error"];
              this.sendErrors.push({
                name: batch.name,
                code: errBody.code || "error",
                messages,
                status: res.status,
              });
            } catch (parseErr) {
              const errText = await res.text();
              this.sendErrors.push({
                name: batch.name,
                code: "error",
                messages: [errText || `HTTP ${res.status}`],
                status: res.status,
              });
            }
            console.error(
              `Batch failed for ${batch.name}:`,
              this.sendErrors[this.sendErrors.length - 1],
            );
          }
        } catch (e) {
          this.sendProgress.errors++;
          this.sendErrors.push({
            name: batch.name,
            code: "network-error",
            messages: [e.message || "Network error"],
            status: 0,
          });
          console.error(`Batch error for ${batch.name}:`, e);
        }

        this.sendProgress.done++;
        this.setStatus(
          "load",
          `Sending: ${this.sendProgress.done}/${
            this.sendProgress.total
          } stallholders`,
        );
      }

      this.sending = false;
      this.setStatus(
        this.sendProgress.errors > 0 ? "err" : "ok",
        `Done. ${this.sendProgress.success} stallholder(s) sent successfully, ${
          this.sendProgress.errors
        } failed.`,
      );
    },

    parseDate(str) {
      const parts = str.split("-");
      return new Date(
        parseInt(parts[0]),
        parseInt(parts[1]) - 1,
        parseInt(parts[2]),
      );
    },

    getWeekOfMonth(date) {
      return Math.ceil(date.getDate() / 7);
    },

    buildWeekLabel(weekNum, isSat) {
      const ordinal =
        weekNum === 1
          ? "1st"
          : weekNum === 2
          ? "2nd"
          : weekNum === 3
          ? "3rd"
          : weekNum + "th";
      return `${ordinal} ${isSat ? "Saturday" : "Sunday"}`;
    },

    getMatchedDates(stallholder) {
      const md = stallholder.marketDay.toLowerCase().replace(/\s+/g, "");
      return this.weekendDates.filter((wd) => {
        if (md.includes("2nd") && md.includes("sun"))
          return wd.isSun && wd.weekNum === 2;
        if (md.includes("2nd") && md.includes("sat"))
          return wd.isSat && wd.weekNum === 2;
        if (md.includes("sat") && md.includes("sun")) return true;
        if (md.includes("sat") && !md.includes("sun")) return wd.isSat;
        if (md.includes("sun") && !md.includes("sat")) return wd.isSun;
        return false;
      });
    },

    dayTagClass(marketDay) {
      const md = marketDay.toLowerCase();
      if (md.includes("sat") && md.includes("sun")) return "tag-both";
      if (md.includes("sat")) return "tag-sat";
      if (md.includes("sun")) return "tag-sun";
      return "";
    },

    formatDate(dateStr) {
      const d = this.parseDate(dateStr);
      const months = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
    },

    setStatus(type, msg) {
      this.statusType = type;
      this.statusMsg = msg;
    },

    exportCSV() {
      const rows = [
        [
          "Stallholder Name",
          "Stallholder ID",
          "Email",
          "Market Day",
          "Stall Type",
          "Stall",
          "Stall ID",
          "Market Date",
          "Day",
          "Week",
        ],
      ];

      this.groupedStallholders.forEach((sh) => {
        sh.matchedDates.forEach((md) => {
          sh.stalls.forEach((stall) => {
            rows.push([
              sh.name,
              sh.stallholderId || "",
              sh.email,
              sh.marketDay,
              sh.stallType,
              stall.name,
              stall.resolvedId || "",
              md.date,
              md.dayName,
              md.weekLabel,
            ]);
          });
        });
      });

      const csv = Papa.unparse(rows);
      const blob = new Blob([csv], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "stallholder-invitations.csv";
      a.click();
      URL.revokeObjectURL(url);
    },
  },
};
</script>

<style scoped>
.bulk-invitations {
  font-family: Arial, sans-serif;
  padding: 20px;
}

.upload-zone {
  border: 2px dashed #ddd;
  border-radius: 5px;
  padding: 48px 32px;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s;
}

.upload-zone:hover,
.upload-zone.dragover {
  border-color: #007bff;
  background: #f0f7ff;
}

.upload-zone input {
  display: none;
}

.upload-zone .icon {
  font-size: 32px;
  margin-bottom: 12px;
  opacity: 0.5;
}

.upload-zone .label {
  font-size: 15px;
  font-weight: 500;
}

.upload-zone .hint {
  font-size: 13px;
  color: #888;
  margin-top: 4px;
}

.config-section {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 5px;
  padding: 20px;
  margin-top: 20px;
}

.config-section h3 {
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #888;
  margin-bottom: 12px;
}

.config-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 10px;
  flex-wrap: wrap;
}

.config-row label {
  font-size: 13px;
  font-weight: 500;
  color: #666;
  min-width: 100px;
}

.config-hint {
  font-size: 12px;
  color: #888;
}

.date-input {
  padding: 5px 8px;
  border: 1px solid #ddd;
  border-radius: 3px;
}

.btn {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  transition: all 0.15s;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-primary {
  background-color: #007bff;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background-color: #0056b3;
}

.btn-secondary {
  background-color: #6c757d;
  color: white;
}

.btn-secondary:hover:not(:disabled) {
  background-color: #545b62;
}

.btn-success {
  background-color: #28a745;
  color: white;
}

.btn-success:hover:not(:disabled) {
  background-color: #218838;
}

.btn-success:disabled {
  background-color: #ccc;
}

.btn-sm {
  padding: 5px 12px;
  font-size: 12px;
}

.btn-bar {
  display: flex;
  gap: 10px;
  margin-top: 16px;
  flex-wrap: wrap;
}

.status-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 16px;
  padding: 10px 14px;
  background: #fff;
  border-radius: 4px;
  font-size: 13px;
  border: 1px solid #dee2e6;
}

.status-bar .dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.dot-ok {
  background: #28a745;
}

.dot-err {
  background: #dc3545;
}

.dot-load {
  background: #007bff;
  animation: pulse 1s infinite;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.3;
  }
}

.filter-bar {
  display: flex;
  gap: 8px;
  margin-top: 24px;
  flex-wrap: wrap;
  align-items: center;
}

.filter-bar .filter-label {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #888;
  margin-right: 4px;
}

.filter-chip {
  padding: 5px 14px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  border: 1px solid #dee2e6;
  background: #fff;
  cursor: pointer;
  transition: all 0.15s;
}

.filter-chip:hover {
  border-color: #007bff;
}

.filter-chip.active {
  background: #343a40;
  color: #fff;
  border-color: #343a40;
}

.search-input {
  padding: 6px 12px;
  border: 1px solid #dee2e6;
  border-radius: 20px;
  font-size: 12px;
  width: 200px;
  margin-left: auto;
}

.search-input:focus {
  outline: none;
  border-color: #007bff;
}

.summary-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 12px;
  margin-top: 20px;
}

.summary-card {
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: 5px;
  padding: 14px 16px;
}

.summary-card .num {
  font-size: 28px;
  font-weight: 700;
  line-height: 1;
}

.summary-card .desc {
  font-size: 12px;
  color: #888;
  margin-top: 4px;
}

.stallholder-card {
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: 5px;
  margin-top: 20px;
  overflow: hidden;
}

.stallholder-card.has-errors {
  border-color: #dc3545;
  border-width: 2px;
}

.header-error {
  background: #f8d7da !important;
  border-bottom-color: #dc3545 !important;
}

.header-error:hover {
  background: #f1c1c6 !important;
}

.error-notice {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.5px;
  padding: 3px 10px;
  background: #dc3545;
  color: #fff;
  border-radius: 4px;
  text-transform: uppercase;
}

.stallholder-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid #dee2e6;
  background: #f8f9fa;
  flex-wrap: wrap;
  cursor: pointer;
  user-select: none;
}

.stallholder-header:hover {
  background: #eef0f2;
}

.collapse-icon {
  font-size: 10px;
  color: #888;
  flex-shrink: 0;
  width: 12px;
}

.header-summary {
  font-size: 12px;
  color: #888;
  font-weight: 400;
}

.expand-controls {
  display: flex;
  gap: 8px;
  margin-top: 16px;
}

.stallholder-header h2 {
  font-size: 15px;
  font-weight: 700;
}

.stallholder-meta {
  display: flex;
  gap: 10px;
  align-items: center;
  font-size: 12px;
  color: #888;
  flex-wrap: wrap;
  margin-left: auto;
}

.stallholder-meta .email-link {
  font-family: monospace;
  font-size: 11px;
  color: #007bff;
  text-decoration: none;
}

.stallholder-meta .email-link:hover {
  text-decoration: underline;
}

.market-day-tag {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
}

.tag-sat {
  background: #edf5f0;
  color: #2d6a4f;
}

.tag-sun {
  background: #f0eefb;
  color: #5a4fcf;
}

.tag-both {
  background: #fdf8eb;
  color: #b8860b;
}

.id-tag {
  font-family: monospace;
  font-size: 10px;
  padding: 2px 8px;
  border-radius: 4px;
}

.id-resolved {
  background: #edf5f0;
  color: #2d6a4f;
}

.id-missing {
  background: #fdf0ec;
  color: #c4572a;
}

.stall-type-label {
  font-size: 11px;
  color: #888;
  padding: 2px 8px;
  background: #f8f9fa;
  border-radius: 4px;
}

.stalls-list {
  padding: 12px 20px;
  font-size: 12px;
  color: #888;
  border-bottom: 1px solid #dee2e6;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
}

.stalls-list strong {
  color: #333;
  font-weight: 600;
}

.stall-badge {
  font-family: monospace;
  font-size: 11px;
  font-weight: 500;
  padding: 2px 8px;
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 4px;
}

.stall-badge.resolved {
  border-color: #2d6a4f;
  background: #edf5f0;
}

.stall-badge.unresolved {
  border-color: #dc3545;
  background: #fdf0ec;
}

.dates-table {
  width: 100%;
  border-collapse: collapse;
}

.dates-table th {
  text-align: left;
  padding: 8px 20px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #888;
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
}

.dates-table td {
  padding: 8px 20px;
  font-size: 13px;
  border-bottom: 1px solid #dee2e6;
}

.dates-table tr:last-child td {
  border-bottom: none;
}

.dates-table tr:hover td {
  background: #f8f9fa;
}

.week-label {
  font-size: 11px;
  color: #888;
}

.batch-payload-preview {
  padding: 12px 20px;
  border-top: 1px solid #dee2e6;
  background: #f8f9fa;
}

.batch-payload-preview strong {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #888;
}

.batch-payload-preview pre {
  font-family: monospace;
  font-size: 11px;
  color: #333;
  margin-top: 6px;
  white-space: pre-wrap;
  word-break: break-all;
}

.date-count {
  font-size: 12px;
  font-weight: 600;
  color: #888;
  padding: 8px 20px;
  border-top: 1px solid #dee2e6;
  background: #f8f9fa;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.empty-state {
  text-align: center;
  padding: 64px 32px;
  color: #888;
}

.progress-bar-container {
  margin-top: 16px;
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: 5px;
  padding: 16px 20px;
}

.progress-bar-container .progress-text {
  font-size: 13px;
  margin-bottom: 8px;
}

.progress-track {
  height: 6px;
  background: #e9ecef;
  border-radius: 3px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: #28a745;
  border-radius: 3px;
  transition: width 0.3s;
}

.progress-fill.has-errors {
  background: #ffc107;
}

.results-summary {
  margin-top: 8px;
  font-size: 12px;
  color: #888;
}

.results-summary .success-count {
  color: #28a745;
  font-weight: 600;
}

.results-summary .error-count {
  color: #dc3545;
  font-weight: 600;
}

.error-log {
  margin-top: 16px;
  background: #fff;
  border: 2px solid #dc3545;
  border-radius: 5px;
  overflow: hidden;
}

.error-log-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  background: #fdf0ec;
  border-bottom: 1px solid #dee2e6;
}

.error-log-header h2 {
  font-size: 14px;
  font-weight: 700;
  color: #dc3545;
}

.error-log-item {
  padding: 10px 20px;
  border-bottom: 1px solid #dee2e6;
}

.error-log-item:last-child {
  border-bottom: none;
}

.error-log-who {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.error-log-who strong {
  font-size: 13px;
}

.error-code {
  font-family: monospace;
  font-size: 10px;
  padding: 1px 6px;
  background: #fdf0ec;
  color: #dc3545;
  border-radius: 4px;
}

.error-status {
  font-family: monospace;
  font-size: 10px;
  color: #888;
}

.error-msg {
  font-size: 12px;
  color: #333;
  padding: 2px 0;
}

.unresolved-section {
  margin-top: 32px;
  background: #fff;
  border: 2px solid #dc3545;
  border-radius: 5px;
  overflow: hidden;
}

.unresolved-header {
  padding: 16px 20px;
  background: #fdf0ec;
  border-bottom: 1px solid #dee2e6;
}

.unresolved-header h2 {
  font-size: 15px;
  font-weight: 700;
  color: #dc3545;
}

.unresolved-header p {
  font-size: 12px;
  color: #888;
  margin-top: 2px;
}

.unresolved-table {
  width: 100%;
  border-collapse: collapse;
}

.unresolved-table th {
  text-align: left;
  padding: 8px 20px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #888;
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
}

.unresolved-table td {
  padding: 8px 20px;
  font-size: 13px;
  border-bottom: 1px solid #dee2e6;
}

.unresolved-table tr:last-child td {
  border-bottom: none;
}

.unresolved-table tr:hover td {
  background: #f8f9fa;
}

.email-mono {
  font-family: monospace;
  font-size: 11px;
  color: #007bff;
}

.no-email {
  font-size: 11px;
  color: #dc3545;
  font-style: italic;
}

.loading {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>
